import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { createPortal } from 'react-dom'

import { NAVIGATION_ICONS } from '@/constants/navigationAssets'
import { useIsMobile } from '@/utils/useMobile'
import CommonButton from '@/components/form/Button/CommonButton'
import BtnGlobalCustomStyles from '@/components/form/Button/button.customStyles'

import { formatDateString } from '@/utils/formatDateString'

import SearchBox from '../SearchBox'
import { SearchBoxRefHandle } from '../SearchBox'
import { Stroker } from '../../SearchNearParking.styled'
import DateTimeControls from '../DateTimeControls/DateTimeControls'
import SearchModal from '../Options/SearchModal'

import {
  OptionsWrapper,
  MobileSearchBoxWrapper,
  MobileSearchWrapper,
  LocationText,
  DateText
} from './ResultSearch.styled'

const ResultSearch = ({
  isFixed,
  market,
  control
}: {
  isFixed?: boolean
  market: string
  control: any
}) => {
  const { isMobile } = useIsMobile()
  const { t } = useTranslation('parkings')
  const placeholder = t('searchNearParking.search.placeholder')
  const translatedSearchBtn = t('searchNearParking.search.button')
  const router = useRouter()
  const searchBoxRef = useRef<SearchBoxRefHandle>(null)

  const [showSearchBox, setShowSearchBox] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up')
  const [isModalOpen, setModalOpen] = useState(false)
  const [settedValue, setSettedValue] = useState('')
  const [settedStartDate, setSettedStartDate] = useState('')
  const [settedEndDate, setSettedEndDate] = useState('')

  useEffect(() => {
    setSettedValue((router?.query?.locationLabel as string) ?? '')
    setSettedStartDate((router?.query?.startDate as string) ?? '')
    setSettedEndDate((router?.query?.endDate as string) ?? '')
  }, [
    router?.query?.locationLabel,
    router?.query?.startDate,
    router?.query?.endDate
  ])

  const openModal = () => setModalOpen(true)
  const closeModal = () => setModalOpen(false)

  const handleScroll = () => {
    const currentScrollY = window.scrollY

    if (currentScrollY < lastScrollY || currentScrollY === 0) {
      setScrollDirection('up')
    } else if (currentScrollY > lastScrollY) {
      setScrollDirection('down')
    }

    if (scrollDirection === 'up' || currentScrollY <= 140) {
      setShowSearchBox(true)
    } else if (scrollDirection === 'down' && currentScrollY >= 140) {
      setShowSearchBox(false)
    }

    setLastScrollY(currentScrollY)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrollDirection, lastScrollY])

  const handleSearchClick = () => {
    if (searchBoxRef.current) {
      searchBoxRef.current.selectFirstOption()
    }
  }

  return isMobile ? (
    <>
      <MobileSearchBoxWrapper
        isFixed={isFixed}
        isVisible={showSearchBox}
        data-test-id="mobile-search-box-wrapper"
        onClick={openModal}
      >
        <OptionsWrapper data-test-id="options-wrapper">
          <CommonButton
            variant="searchWrapper"
            type="submit"
            data-test-id="search-button-wrapper"
            customStyle={BtnGlobalCustomStyles.searchWrapper}
          >
            <img
              src={NAVIGATION_ICONS.searchMobile}
              alt="Search Mobile"
              width="18px"
              loading="lazy"
            />
          </CommonButton>
          <MobileSearchWrapper data-test-id="mobile-search-wrapper">
            <LocationText>{router?.query?.locationLabel}</LocationText>
            <DateText>
              {formatDateString(router?.query?.startDate as string)}
              {' - '}
              {formatDateString(router?.query?.endDate as string)}
            </DateText>
          </MobileSearchWrapper>
        </OptionsWrapper>
      </MobileSearchBoxWrapper>
      {isModalOpen &&
        createPortal(
          <SearchModal
            settedValue={settedValue}
            setSettedValue={setSettedValue}
            setSettedStartDate={setSettedStartDate}
            setSettedEndDate={setSettedEndDate}
            settedStartDate={settedStartDate}
            settedEndDate={settedEndDate}
            onCloseModal={closeModal}
            market={market}
            control={control}
          />,
          document.getElementById('modal-search-mobile')!
        )}
    </>
  ) : (
    <OptionsWrapper data-test-id="options-wrapper">
      <SearchBox market={market} placeholder={placeholder} ref={searchBoxRef} />
      <Stroker />
      <DateTimeControls />
      <CommonButton
        variant="searchWrapper"
        type="submit"
        data-test-id="search-button-wrapper"
        customStyle={BtnGlobalCustomStyles.searchWrapper}
        onClick={handleSearchClick}
      >
        <img src={NAVIGATION_ICONS.search} alt="Search" loading="lazy" />
        <span>{translatedSearchBtn}</span>
      </CommonButton>
    </OptionsWrapper>
  )
}

export default ResultSearch
